import type { ReactNode } from 'react';
import { Icon, type IconName } from '../ui/Icon';
import { Button } from '../ui/primitives';
import { cx, type Tone } from '../ui/primitives/cx';

// Rediseño · Bloque C. Piezas pequeñas que comparten las tarjetas del Inicio. Solo dibujan.

/** Encabezado de tarjeta: título y, si hay algo que contar, su contador o su etiqueta. */
export function CardHead({ id, title, icon, aside }: { id: string; title: string; icon?: ReactNode; aside?: ReactNode }) {
  return (
    <div className="n3-inicio__head">
      <h2 className="n3-inicio__title" id={id}>
        {icon && <span aria-hidden="true" className="n3-inicio__title-icon">{icon}</span>}
        <span>{title}</span>
      </h2>
      {aside && <div className="n3-inicio__head-aside">{aside}</div>}
    </div>
  );
}

/**
 * El contador de una tarjeta. `label` dice qué se cuenta: un número suelto no significa nada leído en
 * voz alta. A la vista va solo el número; lo que se lee es el número con su nombre («18 pendientes»).
 */
export function CountChip({ count, label }: { count: number; label: string }) {
  return (
    <span className="n3-inicio__count">
      <span aria-hidden="true">{count}</span>
      <span className="n3-visually-hidden">{count} {label}</span>
    </span>
  );
}

/** El mosaico de color con un icono dentro. Es adorno: el significado lo lleva el texto de al lado. */
export function Tile({ icon, tone, size = 20 }: { icon: IconName; tone: Tone | 'brand' | 'neutral' | 'surface'; size?: number }) {
  return (
    <span aria-hidden="true" className={cx('n3-inicio__tile', `n3-inicio__tile--${tone}`)}>
      <Icon name={icon} size={size} />
    </span>
  );
}

/** Todo en orden: un recuadro verde con palomita y la frase que lo explica. */
export function DoneBox({ title, text, action }: { title?: string; text: string; action?: ReactNode }) {
  return (
    <div className="n3-inicio__done">
      <span aria-hidden="true" className="n3-inicio__done-icon"><Icon name="success" size={20} /></span>
      <div className="n3-inicio__done-text">
        {title && <strong className="n3-inicio__done-title">{title}</strong>}
        <span>{text}</span>
      </div>
      {action && <div className="n3-inicio__done-action">{action}</div>}
    </div>
  );
}

/**
 * Un importe, con los centavos en chico. El importe es el mismo —llega ya formateado del servidor y
 * no se redondea—: solo cambia el tamaño de los centavos, para que la cifra grande del diseño quepa.
 * Si aun así no cabe (una cifra de millones con la letra ampliada), el renglón se parte ANTES de los
 * centavos, no a media cantidad.
 */
export function Money({ label }: { label: string }) {
  const cut = label.lastIndexOf('.');
  if (cut === -1) return <>{label}</>;
  return (
    <span className="n3-inicio__money">
      <span>{label.slice(0, cut)}</span>
      <wbr />
      <span className="n3-inicio__money-cents">{label.slice(cut)}</span>
    </span>
  );
}

/** Una parte que no llegó, dicha en una línea y con la forma de volver a pedirla. */
export function InlineError({ text, onRetry }: { text: string; onRetry: () => void }) {
  return (
    <div className="n3-inicio__inline-error" role="alert">
      <p className="n3-inicio__inline-error-text">
        <Icon name="warning" size={16} />
        <span>{text}</span>
      </p>
      <Button className="n3-inicio__link" onClick={onRetry} variant="ghost">Reintentar</Button>
    </div>
  );
}
